import React, { useState, useEffect, useMemo } from "react";
import { Button, useTheme, Box, IconButton, Stack, Select, OutlinedInput, Chip, Avatar, MenuItem, Tooltip, Typography, InputLabel, FormControl, CircularProgress, ListSubheader } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import AxionControls from "../../../components/controls/AxionControls";
import GridDatePicker from "../../../components/controls/GridDatePicker";
import Controls from "../../../components/controls/Contols";

import { tokens } from "../../../assets/theme";
import { useMsal, useAccount } from "@azure/msal-react";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import StatBox from "../../../components/controls/StatBox";

// Icons
import BarChartIcon from '@mui/icons-material/BarChart';
import EditNotificationsOutlinedIcon from '@mui/icons-material/EditNotificationsOutlined';
import TimerIcon from "@mui/icons-material/Timer";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
// hooks
import { getAllTaskIncidents, } from "../../../fetchers/taskIncident";
// Chart
import { ResponsiveBar } from '@nivo/bar'
//Language
import messages from "../messages";
import { FormattedMessage, useIntl } from "react-intl";
import { get } from "jquery";


export default function Chart(props) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [year, setYear] = useLocalStorage("IncidentsYear", new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);


    const taskQuery = useQuery(
        ["task"],
        () => getAllTaskIncidents(companyKey, year),
        {
            // staleTime: 60000,
            //refetchInterval: 60000
        }
    );

    // let incidents = taskQuery.data?.filter(f => f.type?.id === 1);
    // let currentIncidents = incidents?.filter(f => f?.incidentDate !== null);
    // currentIncidents = currentIncidents.filter(f => new Date(f.incidentDate).getMonth() + 1 === props.month);

    let todaysDate = dayjs().format("YYYY-MM-DD");
    let currentIncidents = props.currentIncidents
    //console.log("currentIncidents", currentIncidents);

    let total = currentIncidents?.length;
    let open = currentIncidents?.filter(f => f.statusId === 1).length;
    let closed = currentIncidents?.filter(f => f.statusId === 5).length;

    let closedPercent = 0;
    let percentBlue = 0;
    let percentGreen = 0;
    let percentYellow = 0;
    let percentOrange = 0;
    let percentRed = 0;

    if (total === 0) {  // Avoid division by zero

    } else {
        closedPercent = (closed / total) * 100;
        percentGreen = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 4).length / total * 100;
        percentYellow = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 3).length / total * 100;
        percentRed = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 1).length / total * 100;
        percentOrange = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 2).length / total * 100;
        percentBlue = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 5).length / total * 100;
    }


    let severity1 = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 1).length;
    let severity2 = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 2).length;
    let severity3 = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 3).length;
    let severity4 = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 4).length;
    let severity5 = currentIncidents?.filter(f => f.categoryValue?.severityLevel === 5).length;

    let data = [
        {
            "severity": "1",
            [formatMessage(messages.redIncidents)]: severity1,
        },
        {
            "severity": "2",
            [formatMessage(messages.orangeIncidents)]: severity2,
        },
        {
            "severity": "3",
            [formatMessage(messages.yellowIncidents)]: severity3,
        },
        {
            "severity": "4",
            [formatMessage(messages.greenIncidents)]: severity4,
        },
        {
            "severity": "5",
            [formatMessage(messages.blueIncidents)]: severity5,

        },
    ];

    function getMonthName(id) {
        switch (id) {
            case 1:
                return formatMessage(messages.jan);
            case 2:
                return formatMessage(messages.feb);
            case 3:
                return formatMessage(messages.mar);
            case 4:
                return formatMessage(messages.apr);
            case 5:
                return formatMessage(messages.may);
            case 6:
                return formatMessage(messages.jun);
            case 7:
                return formatMessage(messages.jul);
            case 8:
                return formatMessage(messages.aug);
            case 9:
                return formatMessage(messages.sep);
            case 10:
                return formatMessage(messages.oct);
            case 11:
                return formatMessage(messages.nov);
            case 12:
                return formatMessage(messages.dec);
            default:
                return "";
        }
    }
    function getSeverityLevelColor(level) {
        let color = "#D34D47";
        switch (level) {
            case 1:
                color = "#D34D47";
                break;
            case 2:
                color = "#ff9800";
                break;
            case 3:
                color = "#F7E200";
                break;
            case 4:
                color = "#43B328";
                break;
            case 5:
                color = "#6870fa";
                break;
            default:
                break;
        }
        return color;
    }


    //console.log("props.group", props.group);

    return (
        <>

            <Box display="flex" justifyContent="center" alignItems="center" color="#ffffff">
                <Typography variant="h4" color="inherit" align="center">
                    <FormattedMessage {...messages.incidents} /> {getMonthName(month)} {year}
                    {props.group !== undefined ? <span> - {props.group}</span> : ""}
                    {props.department !== undefined ? <span> - {props.department}</span> : ""}
                </Typography>
            </Box>

            <ResponsiveBar
                data={data}
                keys={[
                    formatMessage(messages.redIncidents),
                    formatMessage(messages.orangeIncidents),
                    formatMessage(messages.yellowIncidents),
                    formatMessage(messages.greenIncidents),
                    formatMessage(messages.blueIncidents),
                ]}
                indexBy="severity"
                margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                padding={0.3}
                //maxValue={1000}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                theme={{
                    tooltip: {
                        container: {
                            background: '#333333',
                        },
                    },
                    labels: {
                        text: {
                            fontSize: 18,
                        }
                    },
                    grid: {
                        line: {
                            stroke: '#333333', // Set your desired grid color here
                            strokeWidth: 1,
                        },
                    },
                    axis: {
                        ticks: {
                            text: {
                                fill: '#ffffff', // Set your desired color here
                            },
                        },
                        legend: {
                            text: {
                                fill: '#ffffff', // Set your desired color here
                            },
                        },

                    },
                    legends: {
                        text: {
                            fill: '#ffffff', // Set your desired color here
                        },
                    },
                }}
                //colors={{ scheme: 'nivo' }}
                //colors={['#D34D47', '#F7E200', '#43B328', '#00ff00', '#ff0000']} // Set specific colors for each series
                colors={[getSeverityLevelColor(1), getSeverityLevelColor(2), getSeverityLevelColor(3), getSeverityLevelColor(4), getSeverityLevelColor(5)]} // Set specific colors for each series
                colorBy="id" // Ensure colors are applied by series id
                defs={[
                    {
                        id: 'dots',
                        type: 'patternDots',
                        background: 'inherit',
                        color: '#a8bcb2',
                        size: 4,
                        padding: 1,
                        stagger: true
                    },
                    {
                        id: 'lines',
                        type: 'patternLines',
                        background: 'inherit',
                        color: '#eed312',
                        rotation: -45,
                        lineWidth: 6,
                        spacing: 10
                    }
                ]}
                fill={[
                    {
                        match: {
                            id: 'fries'
                        },
                        id: 'dots'
                    },
                    {
                        match: {
                            id: 'sandwich'
                        },
                        id: 'lines'
                    }
                ]}
                borderColor={{
                    from: 'color',
                    modifiers: [
                        [
                            'darker',
                            1.6
                        ]
                    ]
                }}
                axisTop={null}
                axisRight={null}
                axisBottom={null}
                // axisBottom={{
                //     tickSize: 5,
                //     tickPadding: 5,
                //     tickRotation: 0,
                //     //legend: 'Allvarlighetsgrad',
                //     legendPosition: 'middle',
                //     legendOffset: 32,
                //     truncateTickAt: 0
                // }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    //legend: 'Antal avvikelser',
                    legendPosition: 'middle',
                    legendOffset: -40,
                    truncateTickAt: 0
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor="#ffffff"
                // labelTextColor={{
                //     from: 'color',
                //     modifiers: [
                //         [
                //             'darker',
                //             1.6
                //         ]
                //     ]
                // }}
                legends={[
                    {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        justify: false,
                        translateX: 110,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 110,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                            {
                                on: 'hover',
                                style: {
                                    itemOpacity: 1
                                }
                            }
                        ]
                    }
                ]}
                role="application"
                ariaLabel="Nivo bar chart demo"
                barAriaLabel={e => e.id + ": " + e.formattedValue + " in country: " + e.indexValue}
            />


            <Box margin="15px" marginTop="1px" color="#ffffff"
                //height="120px"
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                gridAutoRows="110px"
                gap="20px">
                <Box
                    gridColumn="span 4"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        height="100px"
                        title={total}
                        subtitle={formatMessage(messages.thisMonthsIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <EditNotificationsOutlinedIcon sx={{ color: '#F7E200', fontSize: "50px" }} />
                        }
                    />
                </Box>
                <Box
                    gridColumn="span 4"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={closed}
                        subtitle={formatMessage(messages.closedIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <TimerOutlinedIcon sx={{ color: "#43B328", fontSize: "50px" }} />
                        }
                    />
                </Box>
                <Box
                    gridColumn="span 4"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={closedPercent.toFixed(0)}
                        subtitle={formatMessage(messages.closedIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <PercentIcon sx={{ color: "#43B328", fontSize: "50px" }} />
                        }
                    />
                </Box>
            </Box>
            <Box margin="15px" color="#ffffff"
                // height="120px"
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                gridAutoRows="110px"
                gap="20px">
                <Box
                    gridColumn="span 2"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={percentRed.toFixed(0) + "%"}
                        subtitle={formatMessage(messages.redIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <PercentIcon sx={{ color: getSeverityLevelColor(1), fontSize: "50px" }} />
                        }
                    />
                </Box>

                <Box
                    gridColumn="span 2"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={percentOrange.toFixed(0) + "%"}
                        subtitle={formatMessage(messages.orangeIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <PercentIcon sx={{ color: getSeverityLevelColor(2), fontSize: "50px" }} />
                        }
                    />
                </Box>
                <Box
                    gridColumn="span 2"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={percentYellow.toFixed(0) + "%"}
                        subtitle={formatMessage(messages.yellowIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <PercentIcon sx={{ color: getSeverityLevelColor(3), fontSize: "50px" }} />
                        }
                    />
                </Box>
                <Box
                    gridColumn="span 2"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={percentGreen.toFixed(0) + "%"}
                        subtitle={formatMessage(messages.greenIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <PercentIcon sx={{ color: getSeverityLevelColor(4), fontSize: "50px" }} />
                        }
                    />
                </Box>
                <Box
                    gridColumn="span 2"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={percentBlue.toFixed(0) + "%"}
                        subtitle={formatMessage(messages.blueIncidents)}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <PercentIcon sx={{ color: getSeverityLevelColor(5), fontSize: "50px" }} />
                        }
                    />
                </Box>
                <Box
                    gridColumn="span 2"
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <StatBox
                        title={""}
                        subtitle={todaysDate}
                        progress="0.75"
                        increase="+14%"
                        icon={
                            <AccessTimeIcon sx={{ color: "#aaaaaa", fontSize: "50px" }} />
                        }
                    />
                </Box>
            </Box>
            <Box height="30px"> </Box>

        </>
    )

}



